import React, { useEffect, useRef, useState } from "react";
import { X, Minus, Maximize2, FlipHorizontal, Camera as CamIcon } from "lucide-react";

/**
 * Floating draggable camera preview. Lives on top of the teleprompter text.
 * Non-blocking (pointer-events on handles only when needed). Minimizable.
 *
 * Props:
 *   - stream: MediaStream to display
 *   - onClose: close the preview (does NOT stop the stream — parent decides)
 *   - onFlip: flip camera (front/back)
 *   - size: "sm" | "md" | "lg"
 *   - recording: boolean — show pulsing dot on preview corner
 *   - elapsed: seconds — formatted mm:ss
 */
const formatMMSS = (s) => {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

const SIZES = {
  sm: { w: 180, h: 135 },
  md: { w: 260, h: 195 },
  lg: { w: 360, h: 270 },
};

export default function CameraFloating({
  stream, onClose, onFlip, recording = false, elapsed = 0,
}) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [pos, setPos] = useState({ x: 16, y: 72 });
  const [size, setSize] = useState("md");
  const [minimized, setMinimized] = useState(false);
  const dragRef = useRef(null);

  // Attach / reattach stream
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream || null;
    if (stream) {
      el.play().catch(() => { /* iOS may require user gesture; ignored */ });
    }
    return () => { if (el) el.srcObject = null; };
  }, [stream]);

  // Clamp inside viewport on resize
  useEffect(() => {
    const clamp = () => {
      setPos((p) => {
        const dim = SIZES[size];
        const maxX = window.innerWidth - dim.w - 8;
        const maxY = window.innerHeight - dim.h - 8;
        return {
          x: Math.min(Math.max(8, p.x), Math.max(8, maxX)),
          y: Math.min(Math.max(8, p.y), Math.max(8, maxY)),
        };
      });
    };
    clamp();
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, [size]);

  const onPointerDown = (e) => {
    const el = containerRef.current;
    if (!el) return;
    el.setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: { ...pos } };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const dim = SIZES[minimized ? "sm" : size];
    const nextX = Math.min(Math.max(8, d.origin.x + dx), window.innerWidth - dim.w - 8);
    const nextY = Math.min(Math.max(8, d.origin.y + dy), window.innerHeight - dim.h - 8);
    setPos({ x: nextX, y: nextY });
  };
  const onPointerUp = (e) => {
    const el = containerRef.current;
    if (el) el.releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
  };

  const dim = SIZES[minimized ? "sm" : size];

  return (
    <div
      ref={containerRef}
      data-testid="camera-floating"
      className="fixed z-[60] rounded-sm overflow-hidden shadow-2xl border border-white/15 bg-black select-none"
      style={{ left: pos.x, top: pos.y, width: dim.w, height: dim.h, touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="w-full h-full object-cover"
        style={{ transform: "scaleX(-1)" }}
      />
      {/* Top toolbar */}
      <div className="absolute inset-x-0 top-0 h-7 bg-gradient-to-b from-black/80 to-transparent flex items-center justify-between px-1">
        <div className="flex items-center gap-1">
          <CamIcon className="w-3 h-3 text-white/80 ml-1" />
          {recording && (
            <span
              data-testid="camera-recording-dot"
              className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-red-400"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              {formatMMSS(elapsed)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          {onFlip && (
            <button
              data-testid="camera-flip"
              onClick={(e) => { e.stopPropagation(); onFlip(); }}
              onPointerDown={(e) => e.stopPropagation()}
              className="w-6 h-6 text-white/80 hover:bg-white/10 rounded-sm flex items-center justify-center"
              title="Virar câmera"
            >
              <FlipHorizontal className="w-3 h-3" />
            </button>
          )}
          <button
            data-testid="camera-minimize"
            onClick={(e) => { e.stopPropagation(); setMinimized((m) => !m); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="w-6 h-6 text-white/80 hover:bg-white/10 rounded-sm flex items-center justify-center"
            title={minimized ? "Expandir" : "Minimizar"}
          >
            {minimized ? <Maximize2 className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
          </button>
          <button
            data-testid="camera-close"
            onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="w-6 h-6 text-white/80 hover:bg-white/10 rounded-sm flex items-center justify-center"
            title="Fechar câmera"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      {/* Bottom size switcher */}
      {!minimized && (
        <div
          className="absolute bottom-1 right-1 flex items-center gap-0.5"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {["sm", "md", "lg"].map((k) => (
            <button
              key={k}
              data-testid={`camera-size-${k}`}
              onClick={(e) => { e.stopPropagation(); setSize(k); }}
              className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm ${size === k ? "bg-white/80 text-black" : "bg-black/40 text-white/70 hover:bg-black/70"}`}
            >
              {k === "sm" ? "P" : k === "md" ? "M" : "G"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
